import { ReactElement } from 'react';

import { CompleteAddress, Country } from '@dreryk/address-common-api';
import {
   CurrentPractitionerUpdateAddressCommand,
   CurrentPractitionerUpdatePhoneNumberCommand,
} from '@dreryk/egabinet-personnel-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction } from '@dreryk/react-common';
import { dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { draftPrescriptionsTranslationKeys, prescriptionsTranslationKeys } from '../../../../../translations';

import {
   isAddressMissing,
   isPhoneNumberMissing,
} from './ProAuctoreAndProFamiliaMissingContactDetailsForm/ProAuctoreAndProFamiliaMissingContactDetailsForm.utils';
import { ProAuctoreAndProFamiliaMissingDetailsFormController } from './ProAuctoreAndProFamiliaMissingContactDetailsForm/ProAuctoreAndProFamiliaMissingDetailsForm.types';
import { ProAuctoreAndProFamiliaAddressSectionFormBeanPart } from './ProAuctoreAndProFamiliaMissingContactDetailsForm/sections/AddressSection/AddressSection.types';
import { ProAuctoreAndProFamiliaPhoneNumberSectionFormBeanPart } from './ProAuctoreAndProFamiliaMissingContactDetailsForm/sections/PhoneNumberSection/PhoneNumberSection.types';
import { ProAuctoreAndProFamiliaMissingDoctorContactDetails } from './ProAuctoreAndProFamiliaMissingContactDetailsModal.types';

type ProAuctoreAndProFamiliaMissingDetailsModalConfirmButtonProps = Pick<
   ProAuctoreAndProFamiliaMissingDetailsFormController,
   'handleSubmit'
> & {
   readonly onMissingDetailsUpdated: PromiseVoidFunction;
   readonly onClose: VoidFunction;
   readonly missingDetails: ProAuctoreAndProFamiliaMissingDoctorContactDetails;
};

export const ProAuctoreAndProFamiliaMissingContactDetailsModalConfirmButton = (
   props: ProAuctoreAndProFamiliaMissingDetailsModalConfirmButtonProps
): ReactElement => {
   const { executeAsync: updateDoctorMissingAddressDetails } = useCqrsCommand<
      void,
      ProAuctoreAndProFamiliaAddressSectionFormBeanPart
   >((parameters) => {
      const command = new CurrentPractitionerUpdateAddressCommand();
      command.address = Choice.of(
         CompleteAddress.builder()
            .withBuilding(parameters.building!)
            .withFlat(parameters.flat)
            .withTown(parameters.town!)
            .withPostcode(parameters.postcode!)
            .withStreet(parameters.street)
            .withCountry(Country.DEFAULT)
            .build()
      );
      return command;
   });
   const { executeAsync: updateDoctorMissingPhoneNumber } = useCqrsCommand<
      void,
      ProAuctoreAndProFamiliaPhoneNumberSectionFormBeanPart
   >((parameters) => {
      const command = new CurrentPractitionerUpdatePhoneNumberCommand();
      command.phoneNumber = Choice.of(parameters.phoneNumber);
      return command;
   });
   return (
      <ModalConfirmButton
         label={prescriptionsTranslationKeys.missingAddressForPAPF.saveAndSendPrescriptions}
         onClick={props.handleSubmit(async (formValues) => {
            if (isPhoneNumberMissing(props.missingDetails)) {
               await updateDoctorMissingPhoneNumber(formValues);
            }
            if (isAddressMissing(props.missingDetails)) {
               await updateDoctorMissingAddressDetails(formValues);
            }
            dispatchSnackbarPositiveEvent(prescriptionsTranslationKeys.missingAddressForPAPF.contactDetailsSaved);
            await props.onMissingDetailsUpdated();
            props.onClose();
         })}
         blockingOperation={draftPrescriptionsTranslationKeys.submittingPrescription}
      />
   );
};
